import { BRANDS, DEFAULT_BRAND_SLUGS, DEFAULT_BRAND_SLUG_SET, VIEW_SLUGS, flatViews } from './dashboardCatalog'
import { ENTRIES_SLUGS } from './entriesRoutes'

// The server-side allow-list of /{brand}/{dashboard} routes, shared by the
// dashboard page and its /{id} detail page.
//
// Derived from BRANDS/VIEW_SLUGS (dashboardCatalog.ts's single source of
// truth) rather than a hardcoded per-brand slug list — that list used to be
// maintained by hand and silently drifted out of sync whenever a view was
// added to the sidebar (e.g. ctt-mail-alert was missing), which meant
// reloading the page on that dashboard redirected back to /[brand]/overview.
// Same reasoning as /entries/[tab]'s VALID_TABS derived from ENTRIES_SLUGS.
const dashboardsByBrand: Record<string, Set<string>> = Object.fromEntries(
  BRANDS.map((b) => [b.id, new Set(flatViews(b.views).map((v) => VIEW_SLUGS[v.id]))]),
)

// Not every brand has an 'overview' view (e.g. CTT dropped it), so an
// invalid dashboard slug falls back to each brand's first sidebar entry
// instead of a hardcoded 'overview'.
const defaultSlugByBrand: Record<string, string> = Object.fromEntries(
  BRANDS.map((b) => [b.id, VIEW_SLUGS[flatViews(b.views)[0].id]]),
)

/** null when /{brand}/{dashboard} is servable, else where to redirect.
 * A brand with no hand-written BRANDS entry is generated from its platform
 * row (dashboardCatalog's brandsForPlatforms) and has exactly the three
 * default dashboards. This runs on the edge with no database, so it cannot
 * check the code against the platform table -- it lets the slug through and
 * the client falls back to its default view if the brand is not real. */
export function dashboardRedirect(brand: string, dashboard: string): string | null {
  const dashboards = dashboardsByBrand[brand]
  if (!dashboards) return DEFAULT_BRAND_SLUG_SET.has(dashboard) ? null : `/${brand}/${DEFAULT_BRAND_SLUGS.cases}`
  return dashboards.has(dashboard) ? null : `/${brand}/${defaultSlugByBrand[brand]}`
}

export const VALID_ENTRIES_TABS: ReadonlySet<string> = new Set(Object.values(ENTRIES_SLUGS))

// Boards whose detail page has its own URL, /{brand}/{slug}/{id} and
// /entries/{slug}/{id} (the view reads the id back with useDetailPath):
// Domain Candidates, Unified Entries and SIBS Custom Mail Alert (whose
// 'mail' and 'mail-entries' boards are the same CampaignsView).
export const DETAIL_SLUGS: ReadonlySet<string> = new Set([
  DEFAULT_BRAND_SLUGS.domains, ENTRIES_SLUGS['entries-domains'],
  DEFAULT_BRAND_SLUGS.unified, ENTRIES_SLUGS['entries-unified'],
  VIEW_SLUGS['sibs-mail'], VIEW_SLUGS['sibs-mail-entries'],
])

// One brand's board with a detail URL whose slug other brands share for a
// board without one: CTT Pilot Overview (/ctt/pilot-overview/{user_entry id});
// SIBS's Pilot Overview has the same slug and no detail.
const BRAND_DETAIL_SLUGS: Record<string, ReadonlySet<string>> = {
  ctt: new Set([VIEW_SLUGS['ctt-vendor-entries']]),
}

export function hasDetailPage(brand: string, dashboard: string): boolean {
  return DETAIL_SLUGS.has(dashboard) || !!BRAND_DETAIL_SLUGS[brand]?.has(dashboard)
}

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
